import { useMemo } from "react";
import { TeamLink } from "./TeamLink";
import {
  type Bracket,
  type BracketEntry,
  type BracketSchedule,
  type Matchup,
  type SeedTeam,
  buildBracket,
  winnerLabel,
} from "./tournament";

// Props: bids (how many teams make the tournament), teams in seed
// order (seed 1 first), nameFor for display, and the sport+division
// routing context so team-name clicks still deep-link into the team
// page. Optional `schedule` overlays the real tournament dates/times
// when they are known for this conference.
export type TournamentBracketProps = {
  bids: number;
  teams: SeedTeam[];
  sport: string;
  division: string;
  conferenceLabel: string;
  schedule?: BracketSchedule;
};

export function TournamentBracket({
  bids,
  teams,
  sport,
  division,
  conferenceLabel,
  schedule,
}: TournamentBracketProps) {
  const bracket = useMemo<Bracket | null>(
    () => buildBracket(bids, teams, schedule),
    [bids, teams, schedule],
  );

  if (!bracket) {
    // Field size we don't have a canned shape for (e.g. 5, 7, 16).
    // Rather than guessing a layout, name the gap explicitly so a
    // user doesn't mistake our silence for "no tournament".
    return (
      <section className="bracket" aria-label="Projected tournament bracket">
        <h3 className="bracket-head">
          {conferenceLabel} Tournament — projected matchups
        </h3>
        <p className="bracket-unsupported">
          {bids}-team bracket shape not yet supported. Standard
          4/6/8/10/12-team formats render automatically; non-standard
          formats need per-conference bracket data.
        </p>
      </section>
    );
  }

  return (
    <section className="bracket" aria-label="Projected tournament bracket">
      <h3 className="bracket-head">
        {conferenceLabel} Tournament — projected matchups
      </h3>
      <p className="bracket-note">
        Matchups derived from current standings; downstream rounds show
        winners once the earlier game is decided.
      </p>
      <div className="bracket-rounds">
        {bracket.rounds.map((round) => (
          <div key={round.index} className="bracket-round">
            <div className="bracket-round-head">
              <span className="bracket-round-name">{round.name}</span>
              {round.dateRange && (
                <span className="bracket-round-dates">{round.dateRange}</span>
              )}
            </div>
            <ul className="bracket-matchups">
              {round.matchups.map((m) => (
                <li
                  key={`${m.round}-${m.slot}`}
                  className="bracket-matchup"
                  aria-label={`${renderAria(m.top)} versus ${renderAria(m.bottom)}`}
                >
                  <MatchupHead matchup={m} />
                  <BracketSide
                    bracket={bracket}
                    entry={m.top}
                    sport={sport}
                    division={division}
                  />
                  <div className="bracket-vs">vs</div>
                  <BracketSide
                    bracket={bracket}
                    entry={m.bottom}
                    sport={sport}
                    division={division}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function BracketSide({
  bracket,
  entry,
  sport,
  division,
}: {
  bracket: Bracket;
  entry: BracketEntry;
  sport: string;
  division: string;
}) {
  if (entry.kind === "winnerOf") {
    return (
      <div className="bracket-side bracket-side-tbd">
        <span className="bracket-seed">—</span>
        <span className="bracket-name bracket-tbd">
          {winnerLabel(bracket, entry)}
        </span>
      </div>
    );
  }
  return (
    <div className="bracket-side">
      <span className="bracket-seed" aria-label={`seed ${entry.seed}`}>
        #{entry.seed}
      </span>
      <TeamLink
        slug={entry.teamSlug}
        sport={sport}
        division={division}
        title={`Open ${entry.shortName} team page`}
      >
        {entry.shortName}
      </TeamLink>
    </div>
  );
}

// MatchupHead renders the small strip above the two teams showing the
// game number plus (if the caller supplied a schedule) the kickoff
// date and time. Game number always renders; date and time render in
// a trailing "· <date> · <time>" tail only when present, so the strip
// stays compact when no schedule has been wired.
function MatchupHead({ matchup }: { matchup: Matchup }) {
  const bits: string[] = [];
  if (matchup.date) bits.push(matchup.date);
  if (matchup.time) bits.push(matchup.time);
  return (
    <div className="bracket-matchup-head">
      <span className="bracket-game-number">Game {matchup.gameNumber}</span>
      {bits.length > 0 && (
        <span className="bracket-matchup-schedule">
          {bits.map((b, i) => (
            <span key={i}>
              {" · "}
              {b}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

function renderAria(entry: BracketEntry): string {
  if (entry.kind === "winnerOf") {
    return `winner of round ${entry.round} game ${entry.slot}`;
  }
  return `seed ${entry.seed} ${entry.shortName}`;
}
